import { ActionResponse } from "@/utils/serverActions/response";
import { handleError } from "@/utils/errorHandler";
import { handleIfServerActionVersionSkew } from "@/utils/serverActions/versionSkew";
import { useCallback } from "react";

export interface ActionHandlerOptions {
  onSuccess?: (data: any) => void;
  onError?: (error: any) => void;
}

export interface ActionHandlerResult<T> {
  success: boolean;
  data?: T;
  error?: {
    code: string;
    message: string;
    details?: any;
  };
}

/**
 * Hook for handling standardized server action responses
 * Provides consistent error handling and optional toast notifications
 */
export function useActionHandler() {
  const handleAction = useCallback(async <T>(
    actionPromise: Promise<ActionResponse<T>>, 
    options?: ActionHandlerOptions
  ): Promise<ActionHandlerResult<T>> => {
    try {
      const result = await actionPromise;
      
      if (result.success) {
        options?.onSuccess?.(result.data);
        
        return { 
          success: true, 
          data: result.data 
        };
      } else {
        const error = result.error!;
        options?.onError?.(error);
        
        return { 
          success: false, 
          error 
        };
      }
    } catch (err) {
      // A new deployment can invalidate the server action IDs held by this
      // already-loaded tab. Recover by reloading to the latest build instead of
      // surfacing it as a generic error. Returns true once a reload is underway.
      if (handleIfServerActionVersionSkew(err, { component: 'useActionHandler', action: 'handleAction' })) {
        return { success: false, error: { code: 'VERSION_SKEW', message: 'Reloading to the latest version' } };
      }

      // Handle unexpected errors (network issues, etc.)
      const unexpectedError = {
        code: 'UNEXPECTED_ERROR',
        message: 'An unexpected error occurred'
      };
      
      options?.onError?.(unexpectedError);

      // Capture error to Sentry with enriched context
      handleError(err, { component: 'useActionHandler', action: 'handleAction' });
      
      return { 
        success: false, 
        error: unexpectedError 
      };
    }
  }, []);

  return { handleAction };
}